"use client";
import { PaperComponent } from "@/components";
import ListView from "@/components/ListView";
import { CurrencyConverter, IMAGES, WEB_COLORS } from "@/lib/constants";
import Navbar from "@/screens/terms-conditions/Navbar";
import {
  Box,
  Container,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Image from "next/image";
import CustomDivider from "../component/custom-divider-component";

function createData(id, illness) {
  return { id, illness };
}

const rows = [
  createData("1", "Cancer of Specified Severity"),
  createData("2", "Kidney Failure Requiring Regular Dialysis"),
  createData("3", "Multiple Sclerosis with Persisting Symptoms"),
  createData("4", "Major Organ / Bone Marrow Transplant"),
  createData("5", "Open Heart Replacement or Repair of Heart Valves"),
  createData("6", "Open Chest CABG"),
  createData("7", "Permanent Paralysis of Limbs"),
  createData(
    "8",
    "Myocardial Infarction (First Heart Attack – of Specific Severity)",
  ),
  createData("9", "Stroke Resulting in Permanent Symptoms"),
  createData("10", "Benign Brain Tumor"),
  createData("11", "Parkinson’s Disease"),
  createData("12", "Coma of Specified Severity"),
  createData("13", "End Stage Liver Failure"),
  createData("14", "Alzheimer’s Disease"),
  createData("15", "Arota Graft Surgery"),
];

const benefitList = [
  {
    title: `EMI Protection`,
    subTitle: `Up to 3 EMIs (Maximum up to ₹ 50,000 each)`,
    benefit: [
      "1st EMI payment: after 5 days of continuous hospitalization.",
      "2nd EMI payment: after 7 days of continuous hospitalization.",
      "3rd EMI payment: after 10 days of continuous hospitalization.",
    ],
  },
  {
    title: "Permanent Total Disability",
    subTitle: "₹50,000",
    benefit: [
      `Common Sum Insured for Permanent Total Disability and Permanent Partial Disability.`,
      `Pay-out for Permanent Partial Disability will be according to the grid based on the nature of disability.`,
    ],
  },
  {
    title: "Temporary Total Disability",
    subTitle: `${CurrencyConverter(5000)} per week (maximum upto 10 weeks)`,
    benefit: [
      `The benefit will be payable only if the total disability period exceeds 3 days.`,
      `Benefit payable maximum up to 10 weeks only.`,
      `The total benefit under the cover will be paid at one go to the insured when he/she will provide the required disability certificate as per Terms & Conditions of the Cover. Alternatively, at the
            request of the insured, he/she will be paid the benefit under this
            cover, at the end of every month if the total period of disability
            exceed one month.`,
    ],
  },
  {
    title: "Loss Of Job",
    subTitle: `3 EMIs (maximum up to ${CurrencyConverter(50000)} each)`,
    benefit: [
      `A fixed benefit of 3 EMI's (maximum up to specified amount) will be payable in case of involuntary unemployment.`,
      `In case of any severance package offered by the employer, the
            unemployment period for the purpose of claim will commence with the
            end of the severance period.`,
      `Only full time permanent employees are covered. Employment on
            contract basis or part time employment or self employment is not
            covered.`,
      `Claim will be payable only if the insured have been employed
            continuously for 6 months before involuntary unemployment.`,
      `Letter from HR confirming the reason is mandatory to claim under
            this benefit.`,
    ],
  },
  {
    title: `Critical Illness Benefit`,
    subTitle: `${CurrencyConverter(50000)}`,
    benefit: [
      `This policy will cover 15 Critical Illness as per the table of
            Critical Illness provided.`,
      `A fixed benefit as specified is payable at the time of first
            diagnosis of Critical Illness during the Coverage Period.`,
    ],
  },
];

const specialConditionList = [
  `The policy is valid for a period of loan tenure as mentioned in the
            Certificate of Insurance or maximum period of 1 year from the
            Insurance start date or when the loan amount is repaid in full
            (whichever is earlier).`,
  `Any pre-existing diseases are not covered.`,
  `Hospitalization due maternity is not covered.`,
  `Any arrears or dues pertaining to EMI/ loan is not covered.`,
  `The insured must be in between 18-60 years of age.`,
  `The policy is offered to the customers of HeroFincorp who have
            availed loans under Instant Personal Loan.`,
  `The insurance company reserves the right to deny any claim arising
            from an accident that occurs while the insured is engaging in an
            activity that violates the laws of the jurisdiction.`,
];

const ackoCards = [
  {
    icon: IMAGES.hospitalIcon,
    label: "Hospitalization",
    subLabel: "covers upto 3 EMIs",
  },
  { icon: IMAGES.heartIcon, label: "Critical Illness", subLabel: "covered" },
  { icon: IMAGES.jobLossIcon, label: "Job Loss", subLabel: "covers 3 EMIs" },
  { icon: IMAGES.disabilityIcon, label: "Disability", subLabel: "covered" },
];

const AckoEmiProtectionPlan = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <>
      <Navbar />

      <Container maxWidth="lg" sx={{ padding: { xs: 2, md: 4 } }}>
        <Grid
          container
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent={"space-between"}
          alignItems={"stretch"}
          bgcolor={"#7C47E1"}
          p={{ xs: 2, md: 4 }}
          color={"#ffffff"}
          borderRadius={"24px"}
          mb={4}
          textAlign={"left"}
          // spacing={2}
        >
          <Grid item md={6} sx={{ pl: 0 }}>
            <Image
              src={IMAGES.ackoLogo}
              alt="acko-icon"
              width={isMobile ? 75 : 125}
            />
            <Typography
              mt={2}
              fontWeight={500}
              fontSize={{ xs: "24px", md: "44px" }}
              width={{ md: "80%", xs: "100%" }}
            >
              EMI Protection Plan
            </Typography>
            <Typography
              fontWeight={{ xs: 350, md: 400 }}
              fontSize={{ xs: "12px", md: "16px" }}
              width={{ md: "80%" }}
              lineHeight={"24px"}
              mt={2}
            >
              Don{`'`}t let unexpected hospitalization put a strain on your
              finances. Our EMI Protection Plan ensures that your loan
              repayments continue smoothly while you focus on recovery.
            </Typography>
          </Grid>
          <Grid
            container
            justifyContent="space-between"
            spacing={2}
            mt={1}
            md={6}
          >
            {ackoCards?.map((card, index) => (
              <Grid key={index} item xs={6} md={6}>
                <Grid
                  container
                  flexDirection={{ xs: "row", md: "column" }}
                  alignItems={{ xs: "center", md: "center" }}
                  justifyContent={{ md: "center" }}
                  columnGap={1}
                  rowGap={1}
                  wrap="nowrap"
                  sx={{
                    bgcolor: { md: "#6E3FC8" },
                    p: { md: 2 },
                    borderRadius: { sm: "20px" },
                    height: { xs: "100%", md: "185px" },
                    width: "100%",
                    color: "#fff",
                  }}
                >
                  <Grid item>
                    <Image
                      src={card.icon}
                      alt={`icon-${index}`}
                      width={isMobile ? 30 : 40}
                      height={isMobile ? 30 : 40}
                    />
                  </Grid>

                  <Grid item textAlign={{ md: "center", xs: "left" }}>
                    <Typography
                      fontWeight={500}
                      fontSize={{ xs: "12px", md: "16px" }}
                    >
                      {card.label}
                    </Typography>
                    <Typography
                      fontWeight={350}
                      fontSize={{ xs: "10px", md: "14px" }}
                    >
                      {card.subLabel}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Grid>
        <CustomDivider
          color="#72727299"
          margin={isMobile ? "20px 0" : "20px 0"}
        >
          <Typography
            fontWeight={400}
            fontSize={{ xs: "12px", md: "24px" }}
            color={"#72727299"}
            mr={"12px"}
            textTransform={"uppercase"}
          >
            Benefits
          </Typography>
        </CustomDivider>
        {benefitList?.map((item, index) => (
          <PaperComponent
            key={index}
            noDrop
            paperStyle={{
              backgroundColor: "#F2F7F799",
              border: "1px solid #E5F1F1",
            }}
          >
            <Typography
              color={WEB_COLORS.fadeColor}
              textAlign={"left"}
              fontWeight={{ xs: 500, md: 700 }}
              fontSize={{ xs: "14px", md: "32px" }}
            >
              {item.title}
            </Typography>
            <Typography
              fontWeight={400}
              fontSize={{ xs: "12px", md: "20px" }}
              color={WEB_COLORS.fadeColor}
              mt={2}
              textAlign={"left"}
            >
              {item.subTitle}
            </Typography>
            <Box ml={2} mt={2}>
              <ListView
                items={item?.benefit}
                style={{
                  fontSize: isMobile ? "12px" : "16px",
                  fontWeight: 400,
                  color: "#3F3F3FCC",
                  lineHeight: isMobile ? "150%" : "24px",
                }}
              />
            </Box>
          </PaperComponent>
        ))}

        <CustomDivider
          color="#72727299"
          margin={isMobile ? "20px 0" : "20px 0"}
        >
          <Typography
            fontWeight={400}
            fontSize={{ xs: "12px", md: "24px" }}
            color={"#72727299"}
            mr={"12px"}
            textTransform={"uppercase"}
          >
            Special Conditions
          </Typography>
        </CustomDivider>
        <Box ml={2}>
          <ListView
            items={specialConditionList}
            style={{
              fontSize: isMobile ? "12px" : "16px",
              fontWeight: 400,
              color: "#3F3F3FCC",
            }}
          />
        </Box>
        <TableContainer component={Paper} elevation={0} sx={{ marginTop: 4 }}>
          <Table size="small" aria-label="a dense table">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#E5F1F1" }}>
                <TableCell
                  sx={{
                    width: "50%",
                    textAlign: "center",
                    fontSize: { xs: "8px", md: "20px" },
                    color: WEB_COLORS.darkTeal,
                    border: "1px solid #F0F0F0",
                    borderRight: "1px solid #72727299",
                  }}
                >
                  Benefit
                </TableCell>
                <TableCell
                  sx={{
                    width: "50%",
                    textAlign: "center",
                    fontSize: { xs: "8px", md: "20px" },
                    color: WEB_COLORS.darkTeal,
                    border: "1px solid #F0F0F0",
                  }}
                >
                  Waiting Period
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow sx={{ backgroundColor: "#ffffff" }}>
                <TableCell
                  sx={{
                    textAlign: "center",
                    fontSize: { xs: "10px", md: "16px" },
                    color: WEB_COLORS.darkTeal,
                    border: "1px solid #F0F0F0",
                    borderRight: "1px solid #72727299",
                  }}
                >
                  Initial No Waiting Period
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: "center",
                    fontSize: { xs: "10px", md: "16px" },
                    color: WEB_COLORS.darkTeal,
                    border: "1px solid #F0F0F0",
                  }}
                >
                  7 Days (except for accidents)
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ marginTop: 2, marginBottom: 4 }}
        >
          <Table size="small" aria-label="a dense table">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#E5F1F1" }}>
                <TableCell
                  sx={{
                    width: "50%",
                    textAlign: "center",
                    fontSize: { xs: "8px", md: "20px" },
                    color: WEB_COLORS.darkTeal,
                    border: "1px solid #F0F0F0",
                    borderRight: "1px solid #72727299",
                  }}
                >
                  Benefit Name
                </TableCell>
                <TableCell
                  sx={{
                    width: "50%",
                    textAlign: "center",
                    fontSize: { xs: "8px", md: "20px" },
                    color: WEB_COLORS.darkTeal,
                    border: "1px solid #F0F0F0",
                  }}
                >
                  Plan Charges (incl. GST)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow sx={{ backgroundColor: "#ffffff" }}>
                <TableCell
                  sx={{
                    textAlign: "center",
                    fontSize: { xs: "10px", md: "16px" },
                    color: WEB_COLORS.darkTeal,
                    border: "1px solid #F0F0F0",
                    borderRight: "1px solid #72727299",
                  }}
                >
                  Plan 1
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: "center",
                    fontSize: { xs: "10px", md: "16px" },
                    color: WEB_COLORS.darkTeal,
                    border: "1px solid #F0F0F0",
                  }}
                >
                  {CurrencyConverter(549.0, 2)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <CustomDivider
          color="#72727299"
          margin={isMobile ? "20px 0" : "20px 0"}
        >
          <Typography
            fontWeight={400}
            fontSize={{ xs: "12px", md: "24px" }}
            color={"#72727299"}
            mr={"12px"}
            textTransform={"uppercase"}
          >
            List of 15 cis
          </Typography>
        </CustomDivider>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ marginTop: 4, marginBottom: 8 }}
        >
          <Table sx={{}} size="small" aria-label="a dense table">
            <TableHead>
              <TableRow
                sx={{ backgroundColor: "#E5F1F1", border: "1px solid #E5F1F1" }}
              >
                <TableCell
                  sx={{
                    fontSize: { xs: "8px", md: "20px" },
                    color: WEB_COLORS.darkTeal,
                    width: "10%",
                    borderRight: "1px solid #72727299",
                  }}
                >
                  S.No.
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: { xs: "8px", md: "20px" },
                    color: WEB_COLORS.darkTeal,
                  }}
                >
                  Critical Illness
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    backgroundColor: "#ffffff",
                    "&:last-child td": {
                      borderBottom: "1px solid #F0F0F0 !important",
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      textAlign: "left",
                      fontSize: { xs: "10px", md: "16px" },
                      color: WEB_COLORS.darkTeal,
                      border: "1px solid #E5F1F1",
                      borderRight: "1px solid #72727299",
                      borderBottom: "1px solid #72727299",
                    }}
                  >
                    {row.id}.
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: { xs: "10px", md: "16px" },
                      color: WEB_COLORS.darkTeal,
                      border: "1px solid #E5F1F1",
                      borderBottom: "1px solid #72727299",
                    }}
                  >
                    {row.illness}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </>
  );
};

export default AckoEmiProtectionPlan;
